import { ShieldCheck, AlertTriangle, CheckCircle2, AlertCircle, XCircle } from "lucide-react";

const STATUS_META = {
  clear: { label: "Clear", color: "text-[#1D504C]", Icon: CheckCircle2 },
  attention: { label: "Needs Attention", color: "text-[#b45309]", Icon: AlertCircle },
  risk: { label: "Risk Found", color: "text-[#b91c1c]", Icon: XCircle },
};

export const LandRatingCard = ({ rating, sample = false }) => {
  const critical = rating.critical_alert;
  return (
    <div
      data-testid="land-rating-card"
      className={`relative border-2 shadow-[0_24px_60px_rgba(17,56,53,0.12)] ${
        critical ? "border-[#dc2626] bg-[#fef2f2]" : "border-[#C1A74A]/60 bg-white"
      }`}
    >
      {sample && (
        <span data-testid="rating-sample-badge" className="absolute -top-3 left-6 bg-[#16324F] text-white text-[0.62rem] font-heading uppercase tracking-[0.22em] px-3 py-1">
          Sample Rating
        </span>
      )}

      <div className={`px-7 py-6 flex items-start justify-between gap-4 ${critical ? "bg-[#dc2626]" : "bg-[#113835]"}`}>
        <div>
          <p className={`font-heading text-[0.68rem] tracking-[0.22em] uppercase ${critical ? "text-white" : "text-[#C1A74A]"}`}>
            Terraa Land Rating
          </p>
          <p className="text-sm text-white/85 font-light mt-1.5" data-testid="rating-property-label">{rating.property_label}</p>
        </div>
        <div className="text-right shrink-0">
          <p className="font-heading text-4xl font-semibold text-white leading-none" data-testid="rating-score">
            {rating.score_100}<span className="text-base text-white/60 font-normal"> /100</span>
          </p>
          <p
            data-testid="rating-grade"
            className="text-[0.65rem] uppercase tracking-[0.18em] mt-1.5 font-heading font-semibold"
            style={{ color: critical ? "#ffffff" : rating.grade?.color === "#1D504C" ? "#8fd4c8" : rating.grade?.color }}
          >
            {rating.grade?.name}
          </p>
        </div>
      </div>

      {critical && (
        <div data-testid="rating-critical-alert" className="bg-[#dc2626] border-t border-white/25 px-7 py-4 flex items-start gap-3">
          <AlertTriangle size={20} strokeWidth={2} className="text-white shrink-0 mt-0.5" />
          <div>
            <p className="font-heading text-sm font-bold text-white uppercase tracking-wide">
              {rating.critical_message || "High Risk: Critical Documents Missing"}
            </p>
            <p className="text-xs text-white/90 font-light mt-1 leading-relaxed">
              The Mother Deed and/or Encumbrance Certificate is missing or disputed. Do not transact until these documents are produced and verified.
            </p>
          </div>
        </div>
      )}

      <ul className="divide-y divide-[#113835]/8" data-testid="rating-rows">
        {rating.rows.map((row) => {
          const meta = STATUS_META[row.status] || STATUS_META.attention;
          return (
            <li key={row.label} data-testid={`rating-row-${row.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="px-7 py-4 flex items-center justify-between gap-4">
              <p className="font-heading text-sm text-[#113835]">{row.label}</p>
              <div className="flex items-center gap-4">
                <div className="hidden sm:block w-24 h-1.5 bg-[#113835]/10">
                  <div
                    className={`h-1.5 ${row.status === "clear" ? "bg-[#1D504C]" : row.status === "attention" ? "bg-[#C1A74A]" : "bg-[#dc2626]"}`}
                    style={{ width: `${Math.max(4, row.score)}%` }}
                  />
                </div>
                <span className={`flex items-center gap-1.5 text-xs font-heading font-semibold ${meta.color}`}>
                  <meta.Icon size={14} strokeWidth={2} /> {meta.label}
                </span>
              </div>
            </li>
          );
        })}
      </ul>

      <div className={`px-7 py-4 border-t ${critical ? "border-[#dc2626]/20" : "border-[#113835]/8"} flex items-center gap-2.5`}>
        <ShieldCheck size={17} strokeWidth={1.75} className="text-[#C1A74A] shrink-0" />
        <p data-testid="rating-verified-badge" className="font-heading text-[0.68rem] uppercase tracking-[0.18em] text-[#113835] font-semibold">
          Verified by Terraa Legal Experts
        </p>
        {rating.source_verified && (
          <span className="ml-auto text-[0.6rem] uppercase tracking-[0.14em] font-heading text-[#1D504C] bg-[#1D504C]/10 px-2 py-1">
            Source Verified
          </span>
        )}
      </div>

      <p data-testid="rating-disclaimer" className={`px-7 pb-5 text-[0.65rem] font-light leading-relaxed ${critical ? "text-[#7f1d1d]/70" : "text-gray-400"}`}>
        Disclaimer: The Terraa Land Rating is an expert opinion based on records examined as of the rating date. It does not constitute legal advice or title insurance.
      </p>
    </div>
  );
};

export const SAMPLE_RATING = {
  score_100: 78,
  grade: { name: "Gold", color: "#C1A74A", description: "Good — minor curative steps advised" },
  critical_alert: false,
  property_label: "Survey No. 214/2, Kolshet Road, Thane",
  rows: [
    { label: "Title History", score: 85, status: "clear" },
    { label: "Encumbrances", score: 80, status: "clear" },
    { label: "Zoning Verification", score: 62, status: "attention" },
    { label: "Tax & Document Compliance", score: 78, status: "clear" },
  ],
};
